import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Input, Label } from '@redpanda/ui';
import { ChecklistItem } from '../types/pm';

/** 檢查清單完成數（看板卡片、表格用；沒有項目回 null） */
export const checklistProgress = (items?: ChecklistItem[] | null) =>
  items && items.length > 0 ? `${items.filter(item => item.done).length}/${items.length}` : null;

/**
 * 任務的檢查清單（子項目）：勾選完成、直接改文字、刪除；底部輸入框按 Enter 新增
 * 在表單裡：按 Enter 只新增項目，不送出整個表單
 */
const TaskChecklist: React.FC<{ value: ChecklistItem[]; onChange: (items: ChecklistItem[]) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const [draft, setDraft] = useState('');

  const update = (id: string, patch: Partial<ChecklistItem>) => onChange(value.map(item => (item.id === id ? { ...item, ...patch } : item)));
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    onChange([...value, { id: `c${Date.now()}`, text, done: false }]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label>{t('pmTasks.checklist.label', { ns: 'pages' })}</Label>
        {value.length > 0 && <span className="text-[13px] text-fg-secondary tabular-nums">{checklistProgress(value)}</span>}
      </div>
      {value.length > 0 && (
        <ul className="mb-2 divide-y divide-line rounded-md border border-line">
          {value.map(item => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-1.5">
              <input
                type="checkbox"
                checked={item.done}
                onChange={e => update(item.id, { done: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                aria-label={item.text}
              />
              <input
                type="text"
                value={item.text}
                onChange={e => update(item.id, { text: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                className={`flex-1 min-w-0 bg-transparent text-sm focus:outline-none ${item.done ? 'line-through text-fg-muted' : 'text-fg'}`}
              />
              <button
                type="button"
                onClick={() => onChange(value.filter(other => other.id !== item.id))}
                className="text-fg-muted hover:text-fg"
                aria-label={t('common.actions.delete')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={t('pmTasks.checklist.placeholder', { ns: 'pages' })}
      />
    </div>
  );
};

export default TaskChecklist;
